/** Four frames a second, made to look like a car.
 *
 * The logger sends a position about every 250 ms, downsampled from its own
 * 60 Hz capture, and a browser draws sixty times a second. Something has to
 * fill the other fifty-six frames, and the honest way to fill them is to draw
 * the car slightly in the past — a little behind the newest frame — so that
 * there is always a frame on each side of the moment being rendered and the
 * position between them is an interpolation rather than a guess. That is the
 * whole trick, and `DELAY_MS` is its price: the picture lags reality by about
 * a third of a second in exchange for never jumping.
 *
 * The delay also decides what happens when a frame is late. The render clock
 * walks on regardless, so it passes the newest sample and the car would have
 * to be extrapolated. It is not: the position is held where it was last
 * known, because a car drawn where nobody said it was is a lie the spectate
 * page has no business telling. The lap clock is different — elapsed time is
 * the one quantity we can be sure kept running — so it carries on counting
 * until `STALL_MS`, at which point the whole picture is marked stale and the
 * screen says so instead of ticking away beside a motionless car.
 *
 * The timing state is a fold over frames rather than anything the service
 * sends. The Durable Object rebuilds every frame from checked values and
 * keeps only the fields in `parseFrame`, so there is no `last_lap` and no
 * `best_lap` on the wire: a lap time is observed here, by watching `lap`
 * roll over and remembering how far the clock had got. Sector times are
 * observed the same way, from the moment `sector` changes. Nothing in this
 * module talks to the network, holds a React hook or touches the DOM, so all
 * of it can be tested by feeding it numbers.
 */

import type { LiveFrame } from '@/lib/types';

// ── the buffer ────────────────────────────────────────────────────────────

/** One frame, and the moment on this machine's clock it is taken to describe. */
export interface Sample {
  at: number;
  frame: LiveFrame;
}

/** The cadence the logger advertises: one frame every 250 ms. */
export const FRAME_MS = 250;

/** How far behind the newest frame the car is drawn. A little more than one
 *  frame period, so an ordinary late arrival still has a pair to sit between. */
export const DELAY_MS = 320;

/** Nothing new for this long and the picture is stale, not live. */
export const STALL_MS = 2000;

/** Frames kept. At 4 Hz that is a quarter of a minute — enough for the trail
 *  the map draws and far less than the object's own ring buffer. */
export const MAX_SAMPLES = 120;

/**
 * Metres of apparent movement between two frames that no car could have made.
 *
 * A driver who resets to the pits, or a logger that reconnects after a gap,
 * produces two consecutive frames a kilometre apart. Interpolating across
 * that would drag the dot smoothly over the scenery; snapping is what really
 * happened. The allowance is generous — twice the distance the reported speed
 * could cover, plus a fixed slack for the frames where speed is not sent.
 */
export const TELEPORT_SLACK_M = 60;

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

export function clamp(value: number, low: number, high: number): number {
  if (value < low) return low;
  if (value > high) return high;
  return value;
}

function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

/** The moment being drawn: `delayMs` behind the wall clock. */
export function renderClock(nowMs: number, delayMs: number = DELAY_MS): number {
  return nowMs - delayMs;
}

/** Add a frame, keeping the buffer in arrival order and bounded. */
export function pushSample(
  samples: readonly Sample[],
  sample: Sample,
  keep: number = MAX_SAMPLES,
): Sample[] {
  const newest = samples[samples.length - 1];
  const next =
    newest && newest.at > sample.at
      ? [...samples, sample].sort((a, b) => a.at - b.at)
      : [...samples, sample];
  return next.length > keep ? next.slice(next.length - keep) : next;
}

/**
 * The ring buffer a joining spectator is handed, placed on this machine's clock.
 *
 * The replay can be half a minute old, and running it forward would show the
 * car where it was rather than where it is. So the newest replayed frame is
 * dated *now* and the rest are spaced behind it by their own `t`, which
 * leaves the render clock pointing at the newest frame from the first paint
 * while the older ones remain available as the trail the map draws.
 */
export function seedSamples(
  frames: readonly LiveFrame[],
  nowMs: number,
  keep: number = MAX_SAMPLES,
): Sample[] {
  const usable = frames.filter((frame) => Number.isFinite(frame.t));
  const newest = usable[usable.length - 1];
  if (!newest) return [];
  const seeded = usable.map((frame) => ({
    at: nowMs - Math.max(0, newest.t - frame.t) * 1000,
    frame,
  }));
  return seeded.length > keep ? seeded.slice(seeded.length - keep) : seeded;
}

/** Frames a second, measured from arrivals rather than believed from the
 *  stream's own claim. Null until there are two of them to measure between. */
export function measuredHz(samples: readonly Sample[], window = 16): number | null {
  const recent = samples.slice(Math.max(0, samples.length - window));
  const first = recent[0];
  const last = recent[recent.length - 1];
  if (!first || !last || recent.length < 2) return null;
  const span = last.at - first.at;
  if (span <= 0) return null;
  return ((recent.length - 1) / span) * 1000;
}

// ── where the car is ──────────────────────────────────────────────────────

/** The car at one instant: continuous quantities interpolated, discrete ones
 *  taken whole from the nearer frame, because a gear is never 4.5. */
export interface CarState {
  x: number;
  z: number;
  speed: number;
  gear: number;
  lap: number;
  /** Seconds into the current lap. */
  lapTime: number;
  sector: number;
  onTrack: boolean;
  /** Seconds since the stream started. */
  t: number;
  /** The render clock has run past the newest frame; the position is held. */
  extrapolated: boolean;
  /** Nothing has arrived for `STALL_MS`; none of this is current. */
  stalled: boolean;
}

/** Whether the step between two frames is movement or a reset. */
export function isTeleport(a: LiveFrame, b: LiveFrame, slackM: number = TELEPORT_SLACK_M): boolean {
  const seconds = Math.max(0, b.t - a.t);
  const gap = Math.hypot(b.x - a.x, b.z - a.z);
  // `speed` arrives in km/h; the plausible distance is the faster of the two
  // readings, doubled, because a frame straddling a braking zone is not a
  // constant-speed frame.
  const fastest = Math.max(a.speed, b.speed) / 3.6;
  return gap > fastest * seconds * 2 + slackM;
}

function held(frame: LiveFrame, carriedSeconds: number, extrapolated: boolean, stalled: boolean): CarState {
  return {
    x: frame.x,
    z: frame.z,
    speed: frame.speed,
    gear: frame.gear,
    lap: frame.lap,
    lapTime: frame.lap_time + carriedSeconds,
    sector: frame.sector,
    onTrack: frame.on_track,
    t: frame.t + carriedSeconds,
    extrapolated,
    stalled,
  };
}

/**
 * The car at `at`, from whichever pair of frames straddles that moment.
 *
 * Before the buffer starts, the first frame; after it ends, the last one with
 * the clock carried forward but the position frozen. In between, a straight
 * interpolation — except across a lap rollover, where `lap_time` falls off a
 * cliff and is taken from the nearer frame instead, and across a reset, where
 * the two positions are not two ends of a journey.
 */
export function interpolateAt(
  samples: readonly Sample[],
  at: number,
  stallMs: number = STALL_MS,
): CarState | null {
  const first = samples[0];
  const last = samples[samples.length - 1];
  if (!first || !last) return null;

  if (at <= first.at) return held(first.frame, 0, false, false);

  if (at >= last.at) {
    const ahead = at - last.at;
    const carried = Math.min(ahead, stallMs) / 1000;
    return held(last.frame, carried, true, ahead > stallMs);
  }

  let before = first;
  let after = last;
  for (let index = 1; index < samples.length; index += 1) {
    const candidate = samples[index];
    const previous = samples[index - 1];
    if (!candidate || !previous) continue;
    if (candidate.at >= at) {
      before = previous;
      after = candidate;
      break;
    }
  }

  const span = after.at - before.at;
  const t = span > 0 ? clamp((at - before.at) / span, 0, 1) : 0;
  const a = before.frame;
  const b = after.frame;
  const near = t < 0.5 ? a : b;
  const jumped = isTeleport(a, b);
  const rolled = a.lap !== b.lap;

  return {
    x: jumped ? near.x : lerp(a.x, b.x, t),
    z: jumped ? near.z : lerp(a.z, b.z, t),
    speed: jumped ? near.speed : lerp(a.speed, b.speed, t),
    gear: near.gear,
    lap: near.lap,
    lapTime: rolled || jumped ? near.lap_time : lerp(a.lap_time, b.lap_time, t),
    sector: near.sector,
    onTrack: near.on_track,
    t: jumped ? near.t : lerp(a.t, b.t, t),
    extrapolated: false,
    stalled: false,
  };
}

// ── what the clock says ───────────────────────────────────────────────────

/** Sectors on the timing strip. GT7's three, and the design's three cells. */
export const SECTORS = 3;

/** A place on the circuit, in world metres. */
export interface Spot {
  x: number;
  z: number;
}

/** Where the car crossed out of a sector, for the map's S1 and S2 marks.
 *  Observed, because no endpoint publishes sector lines. */
export interface SectorMark extends Spot {
  sector: number;
}

/** At most one mark per sector boundary per lap, a few laps deep. */
const MAX_MARKS = 12;

export interface LiveTiming {
  /** The lap number the last folded frame was on. */
  lap: number;
  /** The sector it was in, 1-based. Zero means the logger has not said. */
  sector: number;
  /** Lap time at which each sector ended, seconds; index 0 closes S1. The
   *  last sector has no split of its own — it closes with the lap. */
  splits: ReadonlyArray<number | null>;
  /** The furthest the lap clock has got this lap, which is what the lap was
   *  worth once the next lap starts. */
  peak: number;
  lastLap: number | null;
  lastSplits: ReadonlyArray<number | null>;
  bestLap: number | null;
  /** The quickest each sector has been since this page connected. */
  bestSectors: ReadonlyArray<number | null>;
  marks: readonly SectorMark[];
  /** Where the lap last rolled over — the start/finish line, observed. The
   *  map draws its S/F tick here when the track-data repo has published no
   *  geometry to take one from. */
  startLine: Spot | null;
  /** Laps whose completion this page actually watched. */
  completed: number;
}

function emptySplits(): Array<number | null> {
  return Array.from({ length: SECTORS - 1 }, () => null);
}

export const EMPTY_TIMING: LiveTiming = {
  lap: 0,
  sector: 0,
  splits: emptySplits(),
  peak: 0,
  lastLap: null,
  lastSplits: emptySplits(),
  bestLap: null,
  bestSectors: Array.from({ length: SECTORS }, () => null),
  marks: [],
  startLine: null,
  completed: 0,
};

/** Individual sector times from the cumulative splits and, when the lap is
 *  over, its total. A sector whose start or end was never seen is null: a
 *  spectator who joined mid-lap has missed S1 and should be told so. */
export function sectorsFromSplits(
  splits: ReadonlyArray<number | null>,
  total: number | null,
): Array<number | null> {
  const cumulative: Array<number | null> = [];
  for (let index = 0; index < SECTORS - 1; index += 1) cumulative.push(splits[index] ?? null);
  cumulative.push(total);

  const out: Array<number | null> = [];
  for (let index = 0; index < SECTORS; index += 1) {
    const end = cumulative[index] ?? null;
    const start = index === 0 ? 0 : (cumulative[index - 1] ?? null);
    out.push(end !== null && start !== null && end > start ? round3(end - start) : null);
  }
  return out;
}

function mergeBest(
  best: ReadonlyArray<number | null>,
  latest: ReadonlyArray<number | null>,
): Array<number | null> {
  return Array.from({ length: SECTORS }, (_unused, index) => {
    const seen = latest[index] ?? null;
    const kept = best[index] ?? null;
    if (seen === null) return kept;
    return kept === null || seen < kept ? seen : kept;
  });
}

/** What the lap that just ended was worth. The logger may one day send it;
 *  until then it is how far this page watched the clock get. A lap we joined
 *  part-way through has no total, and inventing one would poison the best. */
function lapTotal(state: LiveTiming, frame: LiveFrame): number | null {
  const reported = frame.last_lap;
  if (typeof reported === 'number' && Number.isFinite(reported) && reported > 0) {
    return round3(reported);
  }
  if (state.completed === 0 && state.splits.every((split) => split === null)) return null;
  return state.peak > 0 ? round3(state.peak) : null;
}

/**
 * One frame folded into the timing state.
 *
 * Three things can happen. The lap number goes up: the previous lap is
 * closed, its sectors are worked out and its time is offered to the best.
 * The lap number goes down: that is not a lap, it is a new stream or a
 * restart, and everything observed so far describes a session that no longer
 * exists. Otherwise it is the same lap, and the only question is whether the
 * sector changed — if it did, the lap clock at that instant is the split and
 * the position at that instant is where the line is.
 */
export function foldFrame(state: LiveTiming, frame: LiveFrame): LiveTiming {
  if (frame.lap < state.lap) {
    return {
      ...EMPTY_TIMING,
      splits: emptySplits(),
      lastSplits: emptySplits(),
      bestSectors: Array.from({ length: SECTORS }, () => null),
      lap: frame.lap,
      sector: frame.sector,
      peak: frame.lap_time,
      startLine: null,
    };
  }

  if (frame.lap > state.lap) {
    const total = lapTotal(state, frame);
    const closed = total === null ? null : sectorsFromSplits(state.splits, total);
    const reportedBest =
      typeof frame.best_lap === 'number' && Number.isFinite(frame.best_lap) && frame.best_lap > 0
        ? round3(frame.best_lap)
        : null;
    const candidates = [state.bestLap, total, reportedBest].filter(
      (value): value is number => value !== null,
    );

    return {
      lap: frame.lap,
      sector: frame.sector,
      splits: emptySplits(),
      peak: frame.lap_time,
      lastLap: total ?? state.lastLap,
      lastSplits: total === null ? state.lastSplits : state.splits,
      bestLap: candidates.length > 0 ? Math.min(...candidates) : null,
      bestSectors: closed ? mergeBest(state.bestSectors, closed) : state.bestSectors,
      marks: state.marks,
      // The first frame of a stream also raises the lap number; only a
      // rollover this page watched says where the line is.
      startLine: state.peak > 0 ? { x: frame.x, z: frame.z } : state.startLine,
      completed: total === null ? state.completed : state.completed + 1,
    };
  }

  const splits = [...state.splits];
  let marks = state.marks;
  let bestSectors = state.bestSectors;

  const closing = state.sector;
  if (frame.sector > closing && closing >= 1 && closing <= splits.length) {
    const index = closing - 1;
    if ((splits[index] ?? null) === null && frame.lap_time > 0) {
      splits[index] = round3(frame.lap_time);
      const appended = [...marks, { sector: closing, x: frame.x, z: frame.z }];
      marks = appended.length > MAX_MARKS ? appended.slice(appended.length - MAX_MARKS) : appended;
      bestSectors = mergeBest(state.bestSectors, sectorsFromSplits(splits, null));
    }
  }

  return {
    ...state,
    sector: frame.sector > 0 ? frame.sector : state.sector,
    splits,
    peak: Math.max(state.peak, frame.lap_time),
    marks,
    bestSectors,
  };
}

// ── what the strip shows ──────────────────────────────────────────────────

/** A sector cell: a time that is finished, a time that is still running, or
 *  a dash for one this page has no honest figure for. */
export type SectorCell =
  | { state: 'done'; seconds: number; best: boolean }
  | { state: 'running'; seconds: number }
  | { state: 'pending' };

/** The three cells, given the timing state and the lap clock as it stands. */
export function sectorCells(timing: LiveTiming, lapTime: number): SectorCell[] {
  const done = sectorsFromSplits(timing.splits, null);
  const cells: SectorCell[] = [];
  let start = 0;

  for (let index = 0; index < SECTORS; index += 1) {
    const seconds = done[index] ?? null;
    if (seconds !== null) {
      const best = timing.bestSectors[index] ?? null;
      cells.push({ state: 'done', seconds, best: best === null || seconds <= best + 1e-6 });
      start += seconds;
      continue;
    }
    const running = timing.sector - 1 === index && Number.isFinite(start) && lapTime > start;
    cells.push(running ? { state: 'running', seconds: lapTime - start } : { state: 'pending' });
    // Past the first sector with no split, no later sector has a start to
    // measure from, so nothing after this can be shown as running.
    start = Number.NaN;
  }

  return cells;
}
